import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomeBannerCarousel.scss';

import { useSubscription } from '../../contexts/SubscriptionContext';
import { computeCanSeeAds, hasCountedImpression, markImpressionCounted } from '../Ads/AdSlot';
import { resolveClickAction } from '../Ads/AdBanner';

function normalizeApiBase(raw) {
  let s = String(raw || '').trim();
  if (!s) return s;
  s = s.replace(/\/+$/, '');
  s = s.replace(/\/api$/i, '');
  return s;
}

const RAW_API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API ||
  (typeof window !== 'undefined' && window.location.hostname.includes('onrender.com')
    ? 'https://cvanto-api.onrender.com'
    : 'http://localhost:5000');

const API_BASE = normalizeApiBase(RAW_API_BASE);
const PLACEMENT = 'homepage_banner_top';
const AUTO_ADVANCE_MS = 5500;
const RESUME_AFTER_INTERACTION_MS = 8000;

// Default Cvanto system banner (2026-09) — canonical creative is
// 1800x600px / 3:1, dropped in as
// src/components/Home/img/banner/cvanto-default-home-banner.(webp|jpg|png)
// and auto-discovered here via require.context, same pattern as brand logos
// in src/utils/brands.js. No file yet => DEFAULT_SYSTEM_BANNER_IMAGE is
// null and the existing CSS gradient + text fallback below renders instead
// — Home never shows an empty/broken banner while waiting on the asset.
const bannerContext = require.context('./img/banner', false, /\.(webp|png|jpe?g)$/);
const defaultBannerKey = bannerContext.keys().find((k) => /cvanto-default-home-banner/i.test(k));
const DEFAULT_SYSTEM_BANNER_IMAGE = defaultBannerKey ? bannerContext(defaultBannerKey) : null;

async function postJson(url, body) {
  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
      keepalive: true,
    });
  } catch {}
}

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// Always-present fallback — Home's permanent promo/ad slot (2026-09) never
// renders empty. This is Cvanto's OWN content, not a paid ad, so it is
// never gated by REACT_APP_ADS_ENABLED (that flag is a separate, deliberate
// business-launch switch for third-party ad placements — see
// PROJECT_CONTEXT.md) or by the ad-free entitlement (Plus/Business users
// are ad-free from third-party clutter, not from Cvanto's own promo).
const SYSTEM_PROMO_SLIDE = {
  id: 'system-promo-default',
  isSystem: true,
  title: 'Знайдіть свій новий образ',
  description: 'Тисячі речей від продавців з усієї України',
  destinationType: 'internal',
  destinationValue: '/search?shoppingOnly=1',
  imageUrl: DEFAULT_SYSTEM_BANNER_IMAGE,
};

// Home's fashion-first permanent promo/ad carousel (2026-09). Reuses the
// EXISTING ads infrastructure end to end — GET /api/ads/serve (the new
// `all=1` param, additive, every other AdSlot placement is untouched),
// POST /api/ads/impression, POST /api/ads/click, AdBanner.js's own
// resolveClickAction — rather than a second ad system. The ONE thing this
// component owns is presentation (carousel chrome) and the always-present
// system promo slide, which is never a real AdCampaigns row — campaigns
// fetched from the server come after it and are subject to the exact same
// ad-free entitlement every other placement already respects.
export default function HomeBannerCarousel() {
  const navigate = useNavigate();
  const { entitlements } = useSubscription();
  const canSeeAds = computeCanSeeAds(entitlements);

  const [campaigns, setCampaigns] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // Per-slide image load state ('loaded' | 'error'), keyed by slide.id — a
  // slide with no entry yet is still loading. Drives the skeleton (shown
  // until 'loaded') and the fallback-to-gradient/text branch on 'error',
  // instead of ever flashing a bare gray box or a broken-image icon.
  const [imageStatus, setImageStatus] = useState({});
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const resumeTimerRef = useRef(null);
  const autoTimerRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    if (!canSeeAds) {
      setCampaigns([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/ads/serve?placement=${encodeURIComponent(PLACEMENT)}&all=1`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
      } catch {
        if (alive) setCampaigns([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [canSeeAds]);

  const slides = useMemo(() => [SYSTEM_PROMO_SLIDE, ...campaigns], [campaigns]);

  // Active-slide impression tracking — same session-dedup helpers AdSlot.js
  // already uses, so a slide that also appears elsewhere on the page in
  // the same session (unlikely for this placement, but a real invariant)
  // never double-counts.
  useEffect(() => {
    const slide = slides[activeIndex];
    if (!slide || slide.isSystem || !slide.id) return;
    if (hasCountedImpression(slide.id)) return;
    markImpressionCounted(slide.id);
    postJson(`${API_BASE}/api/ads/impression`, { campaignId: slide.id });
  }, [activeIndex, slides]);

  const markImageLoaded = useCallback((id) => {
    setImageStatus((prev) => (prev[id] === 'loaded' ? prev : { ...prev, [id]: 'loaded' }));
  }, []);
  const markImageError = useCallback((id) => {
    setImageStatus((prev) => (prev[id] === 'error' ? prev : { ...prev, [id]: 'error' }));
  }, []);

  const scrollToIndex = useCallback((idx) => {
    const el = slideRefs.current[idx];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
    }
  }, []);

  // Auto-advance — slow, pauses on any user interaction with the track and
  // resumes after a quiet period. Fully off under prefers-reduced-motion.
  useEffect(() => {
    if (slides.length <= 1 || prefersReducedMotion()) return undefined;

    autoTimerRef.current = setInterval(() => {
      if (pausedRef.current) return;
      setActiveIndex((prev) => {
        const next = (prev + 1) % slides.length;
        scrollToIndex(next);
        return next;
      });
    }, AUTO_ADVANCE_MS);

    return () => clearInterval(autoTimerRef.current);
  }, [slides.length, scrollToIndex]);

  const pauseAutoAdvance = useCallback(() => {
    pausedRef.current = true;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, RESUME_AFTER_INTERACTION_MS);
  }, []);

  useEffect(() => () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
  }, []);

  // Track which slide is actually visible after a manual swipe (native
  // scroll-snap — no drag library) so dots/impression stay correct.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = slideRefs.current.indexOf(entry.target);
            if (idx !== -1) setActiveIndex(idx);
          }
        }
      },
      { root: track, threshold: [0.6] }
    );

    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [slides.length]);

  const handleSlideClick = useCallback(
    (slide) => {
      if (slide.isSystem) {
        navigate(slide.destinationValue);
        return;
      }
      postJson(`${API_BASE}/api/ads/click`, { campaignId: slide.id });
      const action = resolveClickAction(slide);
      if (action.type === 'internal') navigate(action.value);
      else if (action.type === 'external') window.open(action.value, '_blank', 'noopener,noreferrer');
    },
    [navigate]
  );

  if (!slides.length) return null;

  return (
    <div className="home-banner-carousel">
      <div
        className="home-banner-carousel__track"
        ref={trackRef}
        onPointerDown={pauseAutoAdvance}
        onTouchStart={pauseAutoAdvance}
        onWheel={pauseAutoAdvance}
      >
        {slides.map((slide, idx) => {
          const status = imageStatus[slide.id];
          // A slide whose image failed to load falls back to the gradient +
          // text branch exactly like a slide with no imageUrl at all —
          // never a broken-image icon.
          const hasImage = !!slide.imageUrl && status !== 'error';
          const isLoaded = status === 'loaded';

          return (
            <div
              key={slide.id}
              ref={(el) => {
                slideRefs.current[idx] = el;
              }}
              className="home-banner-carousel__slide"
              role="button"
              tabIndex={0}
              onClick={() => handleSlideClick(slide)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSlideClick(slide);
                }
              }}
            >
              {!slide.isSystem && <div className="home-banner-carousel__badge">Реклама</div>}

              <div
                className={`home-banner-carousel__content ${slide.isSystem ? 'is-system' : ''} ${
                  hasImage ? 'has-image' : ''
                }`}
              >
                {hasImage ? (
                  <>
                    {!isLoaded && <div className="home-banner-carousel__skeleton" aria-hidden="true" />}
                    {/* Real image creative — the text/CTA is drawn into the
                        image itself (campaigns require an upload; the
                        system slide's default banner is a real 1800x600
                        creative once dropped in), so no duplicate
                        headline/button overlay here. Reserved 3:1 box (see
                        .content) means no layout shift while this loads. */}
                    <img
                      ref={(node) => {
                        // Covers the case where the browser already had the
                        // image cached and fires 'load' before this effect
                        // attaches — onLoad alone can miss that.
                        if (node && node.complete && node.naturalWidth > 0) markImageLoaded(slide.id);
                      }}
                      className={`home-banner-carousel__image ${isLoaded ? 'is-loaded' : ''}`}
                      src={slide.imageUrl}
                      alt={slide.title || ''}
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      onLoad={() => markImageLoaded(slide.id)}
                      onError={() => markImageError(slide.id)}
                    />
                  </>
                ) : (
                  <div className="home-banner-carousel__text">
                    {slide.title && <div className="home-banner-carousel__title">{slide.title}</div>}
                    {slide.description && (
                      <div className="home-banner-carousel__desc">{slide.description}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {slides.length > 1 && (
        <div className="home-banner-carousel__dots" role="tablist" aria-label="Слайди банера">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={idx === activeIndex}
              aria-label={`Слайд ${idx + 1}`}
              className={`home-banner-carousel__dot ${idx === activeIndex ? 'is-active' : ''}`}
              onClick={() => {
                pauseAutoAdvance();
                scrollToIndex(idx);
                setActiveIndex(idx);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
